import { Text } from '@avanta/shared/components/Text';
import { AppointmentDurationLabels, SPECIALITY_ICONS } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useFetchSpecialities } from '@avanta/shared/hooks/useFetchSpecialities';
import { formatDateReadable, formatTime } from '@avanta/shared/utils/date';
import { Building, Calendar, Clock, MapPin, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { type Appointment } from 'types/appointments';
import { SessionSummaryItem } from './SessionSummaryItem';

interface SessionDetailsSectionProps {
  appointment: Appointment;
}

export const SessionDetailsSection = ({
  appointment,
}: SessionDetailsSectionProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const params = useParams();
  const patientId = params?.id;
  const { data: specialities } = useFetchSpecialities(patientId ?? '');
  const speciality = specialities?.find(
    speciality => speciality.id === appointment.specialitiesBookable
  );

  const primaryDetails = [
    {
      icon: SPECIALITY_ICONS[speciality?.id as keyof typeof SPECIALITY_ICONS] ?? User,
      value: t(`AppointmentsReserve.Steps.speciality.options.${speciality?.name}`),
    },
    {
      icon: Building,
      value: `${t(
        `AppointmentsReserve.Steps.psychologist.yourSelection.contactMethod.in-person`
      )} (${appointment.sessionDetails?.durationInMinutes ? `${appointment.sessionDetails?.durationInMinutes}min` : `${AppointmentDurationLabels.DURATION_DOUBLE}`})`,
    },
    {
      icon: MapPin,
      value: appointment.center?.city,
    },
  ];

  const secondaryDetails = [
    {
      icon: MapPin,
      value: appointment.center?.name,
      description: `${appointment.center?.address} ${appointment.center?.postalCode} ${appointment.center?.city}`,
    },
  ];

  return (
    <div className="flex gap-2">
      <div className="flex flex-col gap-3 min-w-58">
        <div className="flex gap-2 mb-3">
          <Calendar className="size-4 text-teal-600" strokeWidth={2} />
          {appointment.dateTime && (
            <Text variant="text-md-medium">{formatDateReadable(appointment.dateTime)}</Text>
          )}
        </div>
        {primaryDetails.map((detail, index) => (
          <SessionSummaryItem key={index} icon={detail.icon} label={detail.value ?? ''} />
        ))}
      </div>
      <div className="flex flex-col gap-3 min-w-58">
        <div className="flex gap-2 mb-3">
          <Clock className="size-4 text-teal-600" strokeWidth={2} />
          {appointment.dateTime && (
            <Text variant="text-md-medium">{formatTime(appointment.dateTime)}</Text>
          )}
        </div>
        <div className="flex flex-col gap-6 min-w-58 max-w-74">
          {secondaryDetails.map((detail, index) => (
            <SessionSummaryItem
              key={index}
              icon={detail.icon}
              label={detail.value ?? ''}
              description={detail.description ?? ''}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
